import { QueryKey, useQueryClient } from "@tanstack/vue-query";
import { MaybeRefOrGetter, toValue, computed } from "vue";
import { retryStrategy } from "@/api/config";
import {
  DeleteMutationOptions,
  MutationOptions,
  QueryOptions,
} from "@/api/types";
import { useAppStore } from "@/store/app";

// Check if token is valid based on expiration time
export const isTokenValid = computed(() => {
  const appStore = useAppStore();
  const userInfo = appStore.userInfo;

  if (!userInfo?.exp) return false;

  const expirationTime = userInfo.exp * 1000;
  const currentTime = Date.now();
  const bufferTime = 30 * 1000;

  return currentTime < expirationTime - bufferTime;
});

export const defaultOptions: QueryOptions = {
  enabled: () => isTokenValid.value,
  retry: retryStrategy,
  params: {},
};

export const useQueryOptions = (options: QueryOptions): QueryOptions => {
  return {
    ...defaultOptions,
    ...options,
  };
};

export const useMutationOptions = (options: MutationOptions) => {
  return {
    ...options,
  };
};

export const useDeleteMutationOptions = <T>(
  ids: MaybeRefOrGetter<T | T[]>,
  getDetailKey: (id: T) => QueryKey,
  options: DeleteMutationOptions = {},
) => {
  const queryClient = useQueryClient();
  const { onSuccess, clearCacheOnSuccess = true, ...rest } = options;

  return {
    ...useMutationOptions(rest),
    onSuccess: (responseData: any) => {
      if (clearCacheOnSuccess) {
        const rawIds = toValue(ids);
        const idArray = Array.isArray(rawIds) ? rawIds : [rawIds];
        idArray.forEach((id) =>
          queryClient.removeQueries({ queryKey: getDetailKey(id) }),
        );
      }

      onSuccess?.(responseData);
    },
  };
};

/// This function is used to generate additioanl params to Tanstack Query Key
export const useQueryKey = (options: QueryOptions): string => {
  const params = useQueryParams(options);
  return params.length > 1 ? `?${params}` : "";
};

// Convert query params object to a string
/**
 * Converts query options into a URL query parameter string.
 *
 * @param options - The query options object containing parameters for the request
 * @param options.params - Optional object containing query parameters
 * @param options.params.include - Optional include parameter to be resolved
 * @param options.params.queryStrings - Optional array of additional query strings
 *
 * @returns A string of URL query parameters joined with '&'
 *
 * @example
 * const options = {
 *   params: {
 *     include: ['user', 'posts'],
 *     queryStrings: ['sort=desc', 'limit=10']
 *   }
 * };
 * const queryString = useQueryParams(options);
 * // Returns: "include=user,posts&sort=desc&limit=10"
 */
export const useQueryParams = (options: QueryOptions): string => {
  const params = options.params || {};
  let queryParams: string[] = [];

  if (options.params?.include) {
    const includeParam = resolveIncludeParam(options);
    if (includeParam) {
      queryParams.push(includeParam);
      delete params.include;
    }
  }
  if (options.params?.queryStrings) {
    queryParams = [...queryParams, ...toValue(options.params.queryStrings)];
  }
  return queryParams.join("&");
};

const resolveIncludeParam = (options: QueryOptions) => {
  let include = toValue(options.params?.include);
  if (include?.length) {
    if (!Array.isArray(include)) {
      include = [include];
    }
    return `include=${include.join(",")}`;
  }
};
